import React, { useState, useRef } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { useViewportHeight } from "./useViewportHeight";
import talkingPhaseLogo from "./talkingphase-logo-royal-purple-rgb-2000px-w-72ppi.png";
import bottomLogo from "./Logo-bottom.png";

// Configuration - Replace with your actual values
const CONFIG = {
  API_ENDPOINT:
    process.env.REACT_APP_API_ENDPOINT ||
    "https://your-api-gateway-url.amazonaws.com/prod/submit-code",
  TURNSTILE_SITE_KEY:
    process.env.REACT_APP_TURNSTILE_SITE_KEY || "your-turnstile-site-key",
};

function HomePage() {
  const navigate = useNavigate();
  const [secretCode, setSecretCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [isWinner, setIsWinner] = useState(false);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const turnstileRef = useRef(null);

  // Handle mobile viewport height issues
  useViewportHeight();

  const initializeTurnstile = () => {
    // Clear any existing Turnstile widget first
    if (window.turnstile && turnstileRef.current) {
      // Clear the container
      turnstileRef.current.innerHTML = "";

      // Reset widget ID if exists
      if (turnstileWidgetId !== null) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (e) {
          // Ignore errors if widget doesn't exist
        }
        setTurnstileWidgetId(null);
      }

      // Only initialize if we don't have a valid token
      if (!turnstileToken) {
        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: CONFIG.TURNSTILE_SITE_KEY,
          callback: function (token) {
            setTurnstileToken(token);
            // Auto-submit immediately when Turnstile completes
            submitForm(token);
          },
          "error-callback": function () {
            setMessage({
              text: "Security verification failed. Please retry.",
              type: "error",
            });
            setIsSubmitting(false);
          },
        });
        setTurnstileWidgetId(widgetId);
      }
    }
  };

  const resetTurnstile = () => {
    if (window.turnstile && turnstileWidgetId !== null) {
      try {
        window.turnstile.reset(turnstileWidgetId);
      } catch (e) {
        // If reset fails, reinitialize
        initializeTurnstile();
      }
      setTurnstileToken("");
    }
  };

  const triggerConfetti = () => {
    const end = Date.now() + 3 * 1000; // 3 seconds
    const colors = ["#FFD700", "#FFA500", "#FF8C00", "#D4AF37"]; // Gold color scheme

    const frame = () => {
      if (Date.now() > end) return;

      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.5 },
        colors: colors,
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.5 },
        colors: colors,
      });

      requestAnimationFrame(frame);
    };

    frame();
  };

  const handleNext = () => {
    // Navigate to form with the secret code as URL parameter
    navigate(`/form/${secretCode.trim().toUpperCase()}`);
  };

  const submitForm = async (token) => {
    if (!secretCode.trim()) {
      setMessage({ text: "Please enter your secret code.", type: "error" });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        CONFIG.API_ENDPOINT,
        {
          code: secretCode.trim().toUpperCase(),
          turnstileToken: token,
        },
        {
          timeout: 10000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setMessage({
          text: "Congratulations! You're a winner! 🎉 Click Next to continue.",
          type: "success",
        });
        setIsWinner(true);

        // Trigger confetti animation
        triggerConfetti();
      } else {
        setMessage({
          text: response.data.message || "Invalid code or code already used.",
          type: "error",
        });
        resetTurnstile();
      }
    } catch (error) {
      console.error("Submission error:", error);
      let errorMessage = "An error occurred. Please try again.";

      if (error.code === "ECONNABORTED") {
        errorMessage = "Request timed out. Please try again.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({
        text: errorMessage,
        type: "error",
      });
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!secretCode.trim()) {
      setMessage({ text: "Please enter your secret code.", type: "error" });
      return;
    }

    // Initialize Turnstile if not already done or if token is missing
    if (!turnstileToken) {
      setIsSubmitting(true);
      setMessage({ text: "", type: "" });
      initializeTurnstile();
      return;
    }

    // If we already have a token, submit directly
    setIsSubmitting(true);
    setMessage({ text: "", type: "" });
    await submitForm(turnstileToken);
  };

  return (
    <div className="golden-ticket-container">
      <div className="ticket-frame">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          {/* Horizontal borders (top and bottom) - normal thickness */}
          <path
            d="M94 1L6 1M6 99L94 99"
            stroke="black"
            strokeWidth="2.4"
            vectorEffect="non-scaling-stroke"
          />
          {/* Vertical borders (left and right) - thicker to compensate for distortion */}
          <path
            d="M0 6L0 94M100 6L100 94"
            stroke="black"
            strokeWidth="4.8"
            vectorEffect="non-scaling-stroke"
          />
          {/* Corner decorative curves */}
          <path
            d="M6 1C6 4 4 6 0 6M0 94C4 94 6 96 6 99M94 99C94 96 96 94 100 94M100 6C96 6 94 4 94 1"
            stroke="black"
            strokeWidth="3.0"
            vectorEffect="non-scaling-stroke"
            fill="none"
          />
        </svg>
      </div>
      <div className="ticket-content">
        <img
          src={talkingPhaseLogo}
          alt="Talking Phase"
          className="talking-phase-logo"
        />

        <div className="form-section">
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              className="secret-code-input"
              value={secretCode}
              onChange={(e) => setSecretCode(e.target.value)}
              placeholder="SECRET CODE"
              disabled={isSubmitting}
              maxLength={50}
            />

            {!isWinner ? (
              <button
                type="submit"
                className="claim-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span>
                    CHECKING...
                  </>
                ) : (
                  <span className="claim-button-text">CLAIM</span>
                )}
              </button>
            ) : (
              <button
                type="button"
                className="claim-button"
                onClick={handleNext}
              >
                NEXT
              </button>
            )}

            <div className="turnstile-container">
              <div ref={turnstileRef}></div>
            </div>

            {message.text && (
              <div className={`message ${message.type}`}>{message.text}</div>
            )}
          </form>
        </div>

        <img src={bottomLogo} alt="Logo" className="bottom-logo" />
      </div>
    </div>
  );
}

export default HomePage;
